import type { QuantityValue } from '@/types/crm';

import { CurrencyValueLabel } from './CurrencyValueLabel';

export interface MultiCurrencyValueLabelProps {
  value: (QuantityValue | null | undefined)[] | null | undefined;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl';
  className?: string;
  /** Enable CSS truncation with ellipsis and title tooltip */
  truncate?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
};

/**
 * Build stable, unique React keys for currency elements. Keying on
 * `currency.unit` alone collides when multiple elements share or lack a unit
 * (malformed / null elements). Serialize + per-value occurrence discriminator,
 * without using the array index (banned as a key).
 */
const buildCurrencyKeys = (
  values: (QuantityValue | null | undefined)[]
): string[] => {
  const occurrences = new Map<string, number>();
  return values.map(v => {
    let serialized: string;
    try {
      serialized = JSON.stringify(v) ?? String(v);
    } catch {
      serialized = String(v);
    }
    const occurrence = occurrences.get(serialized) ?? 0;
    occurrences.set(serialized, occurrence + 1);
    return `${serialized}#${occurrence}`;
  });
};

/**
 * Display component for multi-currency values (array of currency amounts).
 * Renders each currency with CurrencyValueLabel, separated by pipes.
 *
 * @example
 * <MultiCurrencyValueLabel
 *   value={[{amount: 100, unit: 'USD'}, {amount: 90, unit: 'EUR'}]}
 * />
 * // Renders: "$100.00 | €90.00"
 */
export const MultiCurrencyValueLabel: React.FC<
  MultiCurrencyValueLabelProps
> = ({ value, fontSize = 'sm', className = '', truncate }) => {
  const sizeClass = fontSizeClasses[fontSize];
  const truncateClasses = truncate
    ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
    : '';

  if (!value || !Array.isArray(value) || value.length === 0) {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  const keys = buildCurrencyKeys(value);

  // An element missing amount or unit (at either the element or null level)
  // is not renderable as currency; it collapses to "—" for that element only.
  const isRenderable = (
    v: QuantityValue | null | undefined
  ): v is QuantityValue => v != null && v.amount != null && v.unit != null;

  // Tooltip text mirrors the visible render — malformed elements contribute "—".
  const titleText = truncate
    ? value
        .map(v => (isRenderable(v) ? `${v.amount} ${v.unit}` : '—'))
        .join(' | ')
    : undefined;

  return (
    <span
      className={`${sizeClass} ${truncateClasses} ${className}`}
      title={titleText}
    >
      {value.map((currency, index) => (
        <span key={keys[index]}>
          {index > 0 && ' | '}
          {isRenderable(currency) ? (
            <CurrencyValueLabel value={currency} fontSize={fontSize} />
          ) : (
            '—'
          )}
        </span>
      ))}
    </span>
  );
};
